import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ROUTES } from "../../constant/routes";
import myLogo from "../../assets/llb360.png";
import { FaAngleDown, FaBars, FaTimes } from "react-icons/fa";

type MenuItem = {
  label: string;
  path?: string;
  children?: {
    label: string;
    path: string;
  }[];
};

const menuItems: MenuItem[] = [
  { label: "Home", path: ROUTES.HOME },
  {
    label: "About",
    children: [
      { label: "About LLB360", path: "/#about" },
      { label: "What we do", path: "/#what-we-do" },
      { label: "How it works", path: "/#how-it-works" },
    ],
  },
  { label: "Mentors", path: "/#mentors" },
  { label: "Subjects", path: ROUTES.ALL_SUBJECTS },
  { label: "Testimonials", path: "/#testimonials" },
  { label: "Blog", path: "/#blog" },
  { label: "Contact us", path: ROUTES.CONTACT_US },
];

const Header: React.FC = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    // If Home (no hash) → scroll to top
    if (location.pathname === "/" && !location.hash) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // If hash exists → scroll to section
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);

      if (el) {
        setTimeout(() => {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 0);
      }
    }
  }, [location]);

  const isSectionActive = (hash: string) =>
    location.pathname === "/" && location.hash === hash;

  const isAboutActive =
    location.pathname === "/" &&
    ["#about", "#what-we-do", "#how-it-works"].includes(location.hash);

  const handleNavClick = () => {
    setMobileOpen(false);
    setAboutOpen(false);
  };

  return (
    <header className="w-full bg-white sticky top-0 z-50 border-b">
      <nav className="max-w-7xl mx-auto px-4">
        {/* Top Bar */}
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <Link to={ROUTES.HOME} onClick={handleNavClick}>
            <img
              src={myLogo}
              alt="llb360 logo"
              className="w-20 h-20 object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <ul className="hidden lg:flex space-x-6">
            {menuItems.map((item) => (
              <li key={item.label} className="relative group">
                {/* Normal route */}
                {item.path && !item.path.startsWith("/#") && (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      isActive
                        ? "font-semibold text-myButtonBg"
                        : "font-medium text-myLabelText hover:text-myButtonBg"
                    }
                  >
                    {item.label}
                  </NavLink>
                )}

                {/* Section route */}
                {item.path?.startsWith("/#") && (
                  <NavLink
                    to={item.path}
                    className={
                      isSectionActive(item.path.replace("/", ""))
                        ? "font-semibold text-myButtonBg"
                        : "font-medium text-myLabelText hover:text-myButtonBg"
                    }
                  >
                    {item.label}
                  </NavLink>
                )}

                {/* About dropdown */}
                {!item.path && (
                  <button
                    type="button"
                    aria-label="About"
                    className={`flex items-center space-x-1 ${
                      isAboutActive
                        ? "font-semibold text-myButtonBg"
                        : "font-medium text-myLabelText hover:text-myButtonBg"
                    }`}
                  >
                    <span>{item.label}</span>
                    <FaAngleDown className="group-hover:rotate-180 transition-transform duration-200" />
                  </button>
                )}

                {/* Desktop Submenu */}
                {item.children && (
                  <ul
                    className="absolute right-0 top-full mt-2 w-48 bg-white border rounded-md shadow-lg
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                  >
                    {item.children.map((child) => (
                      <li key={child.label}>
                        <NavLink
                          to={child.path}
                          className={
                            isSectionActive(child.path.replace("/", ""))
                              ? "block px-4 py-2 font-semibold text-myButtonBg"
                              : "block px-4 py-2 font-medium text-myLabelText hover:bg-gray-100 hover:text-myButtonBg"
                          }
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>

          {/* Desktop Auth */}
          <div className="hidden lg:flex space-x-4">
            <NavLink
              to={ROUTES.LOGIN}
              className="border border-myButtonBg text-myButtonBg px-4 py-2 rounded-md"
            >
              Login
            </NavLink>
            <NavLink
              to={ROUTES.REGISTER}
              className="bg-myButtonBg text-white px-4 py-2 rounded-md"
            >
              Sign Up
            </NavLink>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden text-2xl bg-myButtonBg text-white p-2 rounded-md"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle Mobile Menu"
          >
            {mobileOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t pb-4">
            <ul className="flex flex-col space-y-4 pt-4">
              {menuItems.map((item) => (
                <li key={item.label}>
                  {item.path && !item.path.startsWith("/#") && (
                    <NavLink
                      to={item.path}
                      onClick={handleNavClick}
                      className="block font-medium text-myLabelText"
                    >
                      {item.label}
                    </NavLink>
                  )}

                  {item.path?.startsWith("/#") && (
                    <NavLink
                      to={item.path}
                      onClick={handleNavClick}
                      className={
                        isSectionActive(item.path.replace("/", ""))
                          ? "block font-semibold text-myButtonBg"
                          : "block font-medium text-myLabelText"
                      }
                    >
                      {item.label}
                    </NavLink>
                  )}

                  {!item.path && (
                    <>
                      <button
                        onClick={() => setAboutOpen(!aboutOpen)}
                        className="flex justify-between w-full font-medium text-myLabelText"
                      >
                        <span>{item.label}</span>
                        <FaAngleDown
                          className={`transition-transform ${
                            aboutOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {aboutOpen && (
                        <ul className="pl-4 mt-2 space-y-2">
                          {item.children?.map((child) => (
                            <li key={child.label}>
                              <NavLink
                                to={child.path}
                                onClick={handleNavClick}
                                className={
                                  isSectionActive(child.path.replace("/", ""))
                                    ? "block font-semibold text-myButtonBg"
                                    : "block font-medium text-myLabelText"
                                }
                              >
                                {child.label}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-col space-y-2">
              <NavLink
                to={ROUTES.LOGIN}
                onClick={handleNavClick}
                className="border border-myButtonBg text-myButtonBg px-4 py-2 rounded-md text-center"
              >
                Login
              </NavLink>
              <NavLink
                to={ROUTES.REGISTER}
                onClick={handleNavClick}
                className="bg-myButtonBg text-white px-4 py-2 rounded-md text-center"
              >
                Sign Up
              </NavLink>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
